import { useState } from "react";
import { AnimatedModal } from "../AnimatedModal";
import { CloseIcon } from "../CloseIcon";
import { useWebSocket } from "@/src/contexts/websockets";
import useGqlClient from "@/src/hooks/useGqlClient";
import {
  DealRunner,
  useAccountQuery,
  useCreateDealMutation,
} from "@/src/graphql/generated";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { actions } from "@/src/store/auth/slice";
import { TextInput } from "../tailwind/TextInput";
import { Button } from "../tailwind/Button";
import { DealSideRadio } from "../DealSideRadio";

export function CreateDealModal(props: { open: boolean; onClose: () => void }) {
  const { open, onClose } = props;

  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");

  const [website, setWebsite] = useState("");

  const { subscribeToTopic } = useWebSocket();

  const client = useGqlClient();
  const createDeal = useCreateDealMutation(client);

  const dispatch = useDispatch();
  const history = useHistory();
  const accountQuery = useAccountQuery(client);

  const [dealRunner, setDealRunner] = useState<DealRunner>(DealRunner.Seller);
  const [isSubmitting, setIsSubmitting] = useState(false);

  return (
    <AnimatedModal size="lg" open={open} onClose={onClose}>
      <div>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-concrete-800 font-semibold ">Create new deal</p>
          </div>
          <CloseIcon onClose={onClose} />
        </div>

        <div>
          <form
            onSubmit={async (e) => {
              e.preventDefault();

              if (isSubmitting) return;

              if (!name) {
                setNameError("Name is required");
                return;
              }

              setIsSubmitting(true);

              try {
                await createDeal.mutateAsync(
                  {
                    input: {
                      company: {
                        name,
                        description: "",
                        website,
                      },
                      runner: dealRunner,
                      indexing: true,
                    },
                  },
                  {
                    onSuccess: (data) => {
                      if (data.createDeal) {
                        accountQuery.refetch().then((accountData) => {
                          if (!accountData.data) {
                            return;
                          }

                          dispatch(
                            actions.refreshAccount({
                              account: accountData.data.account,
                            }),
                          );

                          history.push(`/deal/${data.createDeal.id}/dashboard`);
                          setName("");
                          setWebsite("");
                          onClose();

                          subscribeToTopic(`deal:${data.createDeal.id}`);

                          setIsSubmitting(false);
                        });
                      }
                    },
                  },
                );
              } catch (error) {
                setIsSubmitting(false);
              } finally {
              }
            }}
          >
            <div className="mt-2">
              <TextInput
                ignore1p
                error={nameError}
                onChange={(e) => {
                  setNameError("");
                  setName(e.currentTarget.value);
                }}
                value={name}
                label="Name"
                placeholder="Give the deal a name..."
              />

              <TextInput
                onChange={(e) => {
                  setWebsite(e.currentTarget.value);
                }}
                value={website}
                secondaryLabel="Optional"
                label="Website"
                placeholder="https://www.liquidacquire.com"
                margin="m 0 0 0"
              />

              <div className="mt-3">
                <p className="block text-sm font-semibold leading-6 text-gray-900">
                  Side
                </p>
                <DealSideRadio
                  mode="compact"
                  dealRunner={dealRunner}
                  setDealRunner={setDealRunner}
                />
              </div>
            </div>
            <div className="flex justify-end mt-8 gap-x-3">
              <Button
                type="button"
                onClick={() => {
                  history.goBack();
                }}
                variant="neutral"
                text="Cancel"
              />
              <Button
                type="submit"
                variant="positive"
                text="Create deal"
                isLoading={createDeal.isPending || isSubmitting}
                loadingText="Loading..."
                isDisabled={createDeal.isPending || isSubmitting || name === ""}
              />
            </div>
          </form>
        </div>
      </div>
    </AnimatedModal>
  );
}
